import type { LocalAiCapabilities, LocalAiRuntime } from '../types/LocalAiTypes';

type NavigatorWithAiHints = Navigator & {
  deviceMemory?: number;
};

const simdProbe = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
]);

/**
 * What can be known about this device without asking anything.
 *
 * Everything here is a synchronous property read, which is why `webGpuF16` is false: the only
 * way to learn whether the GPU compiles half-precision shaders is to request an adapter, and
 * that is a promise. Until `probeLocalAiCapabilities` has answered, the conservative reading
 * stands — half precision is treated as absent rather than as probably fine.
 */
export function detectLocalAiCapabilities(): LocalAiCapabilities {
  const browser = navigator as NavigatorWithAiHints;
  return {
    webGpu: browser.gpu !== undefined,
    webGpuF16: false,
    wasm: typeof WebAssembly === 'object',
    wasmSimd: typeof WebAssembly === 'object' && WebAssembly.validate(simdProbe),
    hardwareConcurrency: navigator.hardwareConcurrency || 1,
    deviceMemoryGb: browser.deviceMemory,
    mobile: /Android|iPhone|iPad|iPod|Mobile/iu.test(navigator.userAgent),
  };
}

/**
 * The same reading, after the GPU has been asked what it can actually do.
 *
 * Two things only an adapter can tell us, and both of them decide which checkpoint runs.
 * `navigator.gpu` existing is not a GPU: on a machine with no compatible device the adapter
 * request resolves to null, and the honest answer there is that there is no WebGPU. And an
 * adapter that exists still need not carry `shader-f16` — the feature every fp16-quantized
 * export depends on — which is the difference between Whisper Turbo transcribing and Whisper
 * Turbo throwing on its first Cast node.
 */
export async function probeLocalAiCapabilities(): Promise<LocalAiCapabilities> {
  const base = detectLocalAiCapabilities();
  const browser = navigator as NavigatorWithAiHints;
  if (!base.webGpu) return base;
  try {
    const adapter = await browser.gpu.requestAdapter();
    if (!adapter) return { ...base, webGpu: false };
    return { ...base, webGpuF16: adapter.features.has('shader-f16') };
  } catch {
    return { ...base, webGpu: false };
  }
}

export function chooseLocalAiRuntime(capabilities: LocalAiCapabilities): LocalAiRuntime {
  return capabilities.webGpu && !capabilities.mobile && (capabilities.deviceMemoryGb ?? 8) >= 4 ? 'webgpu' : 'wasm';
}

/** Whether this device can run weights that carry fp16 values — see `LocalAiCapabilities`. */
export function supportsHalfPrecision(capabilities: LocalAiCapabilities): boolean {
  return capabilities.webGpu && capabilities.webGpuF16;
}
